import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Navigate,
  useLinkClickHandler,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Col, notification, Row, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";

import {
  checkWebgisUnsuspensionOwner,
  pollWebgisUnsuspension,
  unsuspendWebgis,
} from "../api/webgis";
import { WebGisConnectBlock } from "../components/webgis/WebGisConnectBlock";
import { WebGisCreatingModal } from "../components/webgis/WebGisCreatingModal";
import { WebGisLinkCards } from "../components/webgis/WebGisLinkCards";
import { WebGisSuccessModal } from "../components/webgis/WebGisSuccessModal";
import { userStore } from "../stores/userStore";
import { usePageTitle } from "../utils/usePageTitle";
import { usePendingAction } from "../utils/usePendingAction";

import "./WebGisPage.css";

// DEV: set to true to simulate a blocked (disabled) webgis instance
const DEV_SIMULATE_BLOCKED = false;

export const WebGisPage = observer(function WebGisPage({
  onUnsuspend = unsuspendWebgis,
  onWaitForUnsuspension = pollWebgisUnsuspension,
}: {
  onUnsuspend?: typeof unsuspendWebgis;
  onWaitForUnsuspension?: typeof pollWebgisUnsuspension;
}) {
  const { t } = useTranslation();
  usePageTitle(t("webgis.settings"));
  const navigate = useNavigate();
  const handlePlansClick = useLinkClickHandler<HTMLElement>(
    "/subscription/update/plans",
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const instanceId = searchParams.get("instance_id");
  const [ownership, setOwnership] = useState<{
    instanceId: string;
    result: "owner" | "not_owner" | "error";
  } | null>(null);
  const [resumeRequested, setResumeRequested] = useState(false);
  const resumeController = useRef<AbortController | null>(null);
  const { pending: resuming, run: runResume } = usePendingAction();

  useEffect(() => () => resumeController.current?.abort(), []);

  useEffect(() => {
    if (!instanceId) return;
    const controller = new AbortController();
    checkWebgisUnsuspensionOwner(controller.signal)
      .then((isOwner) => {
        if (!controller.signal.aborted) {
          setOwnership({ instanceId, result: isOwner ? "owner" : "not_owner" });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setOwnership({ instanceId, result: "error" });
        }
      });
    return () => controller.abort();
  }, [instanceId]);

  const [successModalOpen, setSuccessModalOpen] = useState(() => {
    return searchParams.get("webgis-status") === "started";
  });

  const { webgis, webgisLoaded } = userStore;
  const awaitingUnsuspension =
    resumeRequested || webgis?.state_transition === "wait_for_unsuspend";
  const showUnsuspensionInfo = awaitingUnsuspension && !resuming;

  const handleResume = () =>
    runResume(async () => {
      if (awaitingUnsuspension) return;
      const controller = new AbortController();
      resumeController.current = controller;
      let requestAccepted = false;
      try {
        const response = await onUnsuspend(controller.signal);
        if (controller.signal.aborted) return;
        requestAccepted = true;
        setResumeRequested(true);
        const data = await onWaitForUnsuspension(controller.signal);
        if (controller.signal.aborted) return;
        userStore.setWebgis(data);
        const resumed = data.state === "started" && data.state_transition === null;
        setResumeRequested(!resumed);
        if (resumed && response.next_ngw) {
          let redirectUrl = response.next_ngw;
          if (!/^https?:\/\//i.test(redirectUrl)) {
            // Unsuspension can return a hostname without the Web GIS scheme.
            const protocol = new URL(data.url).protocol;
            redirectUrl = `${protocol}//${redirectUrl.replace(/^\/\//, "")}`;
          }
          window.location.href = redirectUrl;
        }
      } catch {
        if (!controller.signal.aborted && !requestAccepted) {
          notification.error({ message: t("errors.shared.common_error") });
        }
      } finally {
        if (resumeController.current === controller) resumeController.current = null;
      }
    });

  // Resolve the originating Web GIS before displaying the current user's instance
  // or redirecting users without an instance to the creation form.
  if (instanceId) {
    if (ownership?.instanceId !== instanceId) {
      return (
        <div className="page webgis-page__center">
          <Spin size="large" />
        </div>
      );
    }
    if (ownership.result === "not_owner") {
      return (
        <Navigate
          to={`/profile?next=${encodeURIComponent(`contact${instanceId}`)}`}
          replace
        />
      );
    }
    if (ownership.result === "error") {
      return (
        <div className="page">
          <Alert type="error" showIcon title={t("errors.shared.common_error")} />
        </div>
      );
    }
  }

  if (webgisLoaded && !webgis) {
    return <Navigate to="/webgis/create" replace />;
  }

  if (webgisLoaded && webgis?.state_transition === "wait_for_create") {
    return <Navigate to="/webgis/create" replace />;
  }

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    setSearchParams({}, { replace: true });
  };

  const plan = userStore.subscription?.plan ?? "lemon";

  const urlWithoutScheme = webgis?.url.replace(/^https?:\/\//i, "");

  return (
    <div className="page webgis-page">
      <h1>{t("webgis.settings")}</h1>
      {!webgisLoaded && (
        <div className="webgis-page__center">
          <Spin size="large" />
        </div>
      )}
      {webgisLoaded && webgis && (
        <>
          <p>{t("webgis.settingsDescription")}</p>

          {webgis.state_transition === "wait_for_create" ? (
            <div className="webgis-page__creating">
              <p>
                {t("webgis.isBeingCreated")}{" "}
                <a href={`mailto:${userStore.profile?.email}`}>
                  {userStore.profile?.email}
                </a>
              </p>
              <p
                className="webgis-page__creating-hint"
                dangerouslySetInnerHTML={{
                  __html: t("webgis.creatingProblem"),
                }}
              />
            </div>
          ) : (
            <>
              <div className="webgis-page__address-box">
                <span className="webgis-page__address-label">
                  {t("webgis.address")}
                </span>
                <span className="webgis-page__address-separator">—</span>
                <a
                  href={webgis.url_oauth}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="webgis-page__address-link"
                >
                  {urlWithoutScheme}
                </a>
              </div>

              {(webgis.state === "disabled" || DEV_SIMULATE_BLOCKED) && (
                <Alert
                  type="error"
                  className="webgis-page__blocked-alert"
                  description={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("webgis.isBlocked"),
                      }}
                      onClick={(e) => {
                        const a = (e.target as HTMLElement).closest("a");
                        const href = a?.getAttribute("href");
                        if (href?.startsWith("/")) {
                          e.preventDefault();
                          navigate(href);
                        }
                      }}
                    />
                  }
                  showIcon
                />
              )}
              {showUnsuspensionInfo ? (
                <Alert
                  type="info"
                  className="webgis-page__blocked-alert webgis-page__suspended-alert"
                  description={t("webgis.unsuspend.checkLater")}
                  showIcon
                />
              ) : webgis.state === "suspended" && (
                <Alert
                  type="warning"
                  icon={<WarningOutlined />}
                  className="webgis-page__blocked-alert webgis-page__suspended-alert"
                  title={<strong>{t("webgis.isSuspended")}</strong>}
                  description={
                    <>
                      <p className="webgis-page__suspended-description">
                        <Trans
                          i18nKey="webgis.unsuspend.description"
                          components={{
                            lnk: (
                              <Typography.Link
                                href="/subscription/update/plans"
                                onClick={handlePlansClick}
                                underline
                              />
                            ),
                          }}
                        />
                      </p>
                      <div className="webgis-page__resume-actions">
                        <Button
                          type="primary"
                          loading={resuming}
                          onClick={handleResume}
                        >
                          {t("webgis.unsuspend.resume")}
                        </Button>
                      </div>
                    </>
                  }
                  showIcon
                />
              )}
            </>
          )}

          <div className="webgis-page__related">
            <h2>{t("webgis.easyStart")}</h2>
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={14}>
                <WebGisConnectBlock />
              </Col>
              <Col xs={24} sm={10}>
                <WebGisLinkCards />
              </Col>
            </Row>
          </div>

          <WebGisSuccessModal
            open={successModalOpen}
            onClose={handleSuccessModalClose}
            webgisUrl={webgis.url_oauth}
            plan={plan}
          />
        </>
      )}
      <WebGisCreatingModal open={resuming} title={t("webgis.unsuspend.inProgress")} />
    </div>
  );
});
